import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Mail, Plus, X, Users, CheckCircle2, ArrowRight } from "lucide-react";
import { toast } from "sonner";

interface InviteTeamStepProps {
  count: number;
  onComplete: () => void;
}

const ROLES = ["Editor", "Viewer", "Admin"] as const;
type Role = typeof ROLES[number];

interface Invite { email: string; role: Role; }

export function InviteTeamStep({ count, onComplete }: InviteTeamStepProps) {
  const [invites, setInvites] = useState<Invite[]>([]);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<Role>("Editor");

  const addInvite = () => {
    const trimmed = email.trim();
    if (!trimmed || !/^\S+@\S+\.\S+$/.test(trimmed)) { toast.error("Enter a valid email address"); return; }
    if (invites.some(i => i.email.toLowerCase() === trimmed.toLowerCase())) { toast.error("That teammate is already invited"); return; }
    setInvites(prev => [...prev, { email: trimmed, role }]);
    setEmail("");
  };

  const removeInvite = (target: string) => setInvites(prev => prev.filter(i => i.email !== target));

  const handleSendAndContinue = () => {
    if (invites.length > 0) toast.success(`Sent ${invites.length} invite${invites.length === 1 ? "" : "s"}`);
    onComplete();
  };

  return (
    <div className="max-w-3xl mx-auto space-y-8 py-8">
      <div className="text-center space-y-3">
        <div className="mx-auto h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
          <Users className="h-8 w-8 text-primary" />
        </div>
        <h2 className="text-3xl font-bold">Invite your team</h2>
        <p className="text-lg text-muted-foreground">
          Your {count} data points are ready. Bring teammates in so they can access requirements together.
        </p>
      </div>

      <Card>
        <CardContent className="p-6 space-y-4">
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="email"
                placeholder="teammate@company.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addInvite(); } }}
                className="pl-9"
              />
            </div>
            <select value={role} onChange={e => setRole(e.target.value as Role)} className="h-10 rounded-md border border-input bg-background px-3 text-sm">
              {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
            <Button onClick={addInvite} variant="outline" className="gap-2"><Plus className="h-4 w-4" />Add</Button>
          </div>

          {invites.length > 0 ? (
            <div className="space-y-2 pt-2">
              {invites.map(inv => (
                <div key={inv.email} className="flex items-center justify-between rounded-md border bg-muted/30 px-3 py-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />
                    <span className="text-sm truncate">{inv.email}</span>
                    <Badge variant="secondary" className="text-xs ml-1">{inv.role}</Badge>
                  </div>
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => removeInvite(inv.email)}><X className="h-4 w-4" /></Button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center pt-2">No invites yet — you can always add teammates later from Settings.</p>
          )}
        </CardContent>
      </Card>

      <div className="flex flex-col items-center gap-3">
        <Button size="lg" onClick={handleSendAndContinue} className="gap-2">
          {invites.length > 0 ? `Send ${invites.length} invite${invites.length === 1 ? "" : "s"} & continue` : "Skip for now"}
          <ArrowRight className="h-4 w-4" />
        </Button>
        <p className="text-xs text-muted-foreground">You can manage your team anytime from Settings → People.</p>
      </div>
    </div>
  );
}
